import { useCallback, useContext } from 'react';
import {
  VoiceChannelContext,
  type VoiceChannelValue,
} from './VoiceChannelProvider';

const EMPTY_TRANSCRIPTS = Object.freeze({});

export const useVoiceChannel = (conversationId: number): VoiceChannelValue => {
  const channel = useContext(VoiceChannelContext);
  if (!channel) {
    throw new Error('useVoiceChannel must be used within VoiceChannelProvider');
  }
  const isCurrentConversation = channel.activeConversationId === conversationId;
  const hasMatchingSession =
    isCurrentConversation && channel.session?.conversationId === conversationId;
  const pause = useCallback(
    () => channel.pause(conversationId),
    [channel.pause, conversationId],
  );
  const resume = useCallback(
    () => channel.resume(conversationId),
    [channel.resume, conversationId],
  );
  const switchAgent = useCallback(
    (agentId: number) => channel.switchAgent(agentId, conversationId),
    [channel.switchAgent, conversationId],
  );
  const beginPtt = useCallback(
    () => channel.beginPtt(conversationId),
    [channel.beginPtt, conversationId],
  );
  const commitPtt = useCallback(
    () => channel.commitPtt(conversationId),
    [channel.commitPtt, conversationId],
  );
  const cancelPtt = useCallback(
    () => channel.cancelPtt(conversationId),
    [channel.cancelPtt, conversationId],
  );
  const interrupt = useCallback(
    () => channel.interrupt(conversationId),
    [channel.interrupt, conversationId],
  );
  const end = useCallback(
    () => channel.end(conversationId),
    [channel.end, conversationId],
  );
  const returnToText = useCallback(
    () => channel.returnToText(conversationId),
    [channel.returnToText, conversationId],
  );
  const open = useCallback(
    (input: Parameters<typeof channel.openForConversation>[1]) =>
      channel.openForConversation(conversationId, input),
    [channel.openForConversation, conversationId],
  );
  return {
    activeConversationId: channel.activeConversationId,
    session: hasMatchingSession ? channel.session : null,
    status:
      isCurrentConversation &&
      channel.session &&
      channel.session.conversationId !== conversationId
        ? 'opening'
        : isCurrentConversation
          ? channel.status
          : 'idle',
    providerUsageObservationGeneration: isCurrentConversation
      ? channel.providerUsageObservationGeneration
      : 0,
    modelState: hasMatchingSession ? channel.modelState : null,
    muted: hasMatchingSession ? channel.muted : false,
    bargeInAt: hasMatchingSession ? channel.bargeInAt : null,
    setMuted: channel.setMuted,
    inputLevel: hasMatchingSession ? channel.inputLevel : 0,
    outputLevel: hasMatchingSession ? channel.outputLevel : 0,
    inputSpectrum: hasMatchingSession ? channel.inputSpectrum : null,
    outputSpectrum: hasMatchingSession ? channel.outputSpectrum : null,
    provisionalTranscripts: hasMatchingSession
      ? channel.provisionalTranscripts
      : EMPTY_TRANSCRIPTS,
    mediaWarning: hasMatchingSession ? channel.mediaWarning : null,
    error:
      isCurrentConversation &&
      (!channel.session || channel.session.conversationId === conversationId)
        ? channel.error
        : null,
    pause,
    resume,
    switchAgent,
    beginPtt,
    commitPtt,
    cancelPtt,
    interrupt,
    end,
    returnToText,
    conversationId,
    isCurrentConversation,
    open,
  };
};
